import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { violationChargeService } from '../../../services/violationChargeService';

const AddViolationChargeModal = ({ booking, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    chargeAmount: '',
    violationReason: '',
    violationDescription: '',
  });
  const [documentationFiles, setDocumentationFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    processFiles(files);
  };

  const processFiles = (files) => {
    const validFiles = files?.filter(file => {
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file?.size > maxSize) {
        setError(`File ${file?.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });

    const newFiles = validFiles?.map(file => ({
      id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      file,
      name: file?.name,
      size: file?.size,
      preview: file?.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    setDocumentationFiles(prev => [...prev, ...newFiles]);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files || []);
    processFiles(files);
  };

  const handleRemoveFile = (fileId) => {
    setDocumentationFiles(prev => prev?.filter(f => f?.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    // Validation
    if (!formData?.chargeAmount || parseFloat(formData?.chargeAmount) <= 0) {
      setError('Please enter a valid charge amount');
      return;
    }

    if (!formData?.violationReason?.trim()) {
      setError('Please provide a violation reason');
      return;
    }

    if (!formData?.violationDescription?.trim()) {
      setError('Please provide a detailed description');
      return;
    }

    if (documentationFiles?.length === 0) {
      setError('Please upload at least one documentation file');
      return;
    }

    try {
      setIsSubmitting(true);

      const chargeData = {
        bookingId: booking?.id,
        renterId: booking?.renterId,
        chargeAmount: parseFloat(formData?.chargeAmount),
        violationReason: formData?.violationReason,
        violationDescription: formData?.violationDescription,
        documentationFiles: documentationFiles?.map(f => f?.file),
      };

      const result = await violationChargeService?.createCharge(chargeData);

      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error('Submit charge error:', err);
      setError(err?.message || 'Failed to create violation charge');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground">Add Violation Charge</h2>
            <p className="font-body text-sm text-muted-foreground mt-1">
              Booking: {booking?.bookingReference}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-smooth"
          >
            <Icon name="X" size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-danger flex-shrink-0 mt-0.5" strokeWidth={2} />
              <p className="font-body text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Charge Amount */}
          <div>
            <label className="font-body font-medium text-foreground block mb-2">
              Charge Amount <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-body">
                $
              </span>
              <Input
                type="number"
                name="chargeAmount"
                value={formData?.chargeAmount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="pl-8"
                required
              />
            </div>
            <p className="font-caption text-xs text-muted-foreground mt-1">
              Enter the amount to charge the renter for the violation
            </p>
          </div>

          {/* Violation Reason */}
          <div>
            <label className="font-body font-medium text-foreground block mb-2">
              Violation Reason <span className="text-danger">*</span>
            </label>
            <Input
              type="text"
              name="violationReason"
              value={formData?.violationReason}
              onChange={handleInputChange}
              placeholder="e.g., Late return, Damage to trailer, Cleaning required"
              required
            />
            <p className="font-caption text-xs text-muted-foreground mt-1">
              Brief summary of the violation (shown to renter)
            </p>
          </div>

          {/* Violation Description */}
          <div>
            <label className="font-body font-medium text-foreground block mb-2">
              Detailed Description <span className="text-danger">*</span>
            </label>
            <textarea
              name="violationDescription"
              value={formData?.violationDescription}
              onChange={handleInputChange}
              placeholder="Provide a detailed explanation of the violation, including dates, times, and specific issues..."
              rows={4}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-smooth font-body text-foreground bg-background"
              required
            />
            <p className="font-caption text-xs text-muted-foreground mt-1">
              Detailed explanation with supporting information
            </p>
          </div>

          {/* Documentation Upload */}
          <div>
            <label className="font-body font-medium text-foreground block mb-2">
              Documentation <span className="text-danger">*</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-smooth ${
                isDragging
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Upload" size={24} color="var(--color-primary)" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-body text-sm text-foreground mb-1">
                    Drag and drop files here, or
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('file-input')?.click()}
                    iconName="Upload"
                  >
                    Browse Files
                  </Button>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
                <p className="font-caption text-xs text-muted-foreground">
                  Upload photos, videos, or documents (Max 10MB per file)
                </p>
              </div>
            </div>

            {/* File List */}
            {documentationFiles?.length > 0 && (
              <div className="mt-4 space-y-2">
                {documentationFiles?.map((file) => (
                  <div
                    key={file?.id}
                    className="flex items-center space-x-3 p-3 bg-muted rounded-lg"
                  >
                    {file?.preview ? (
                      <img
                        src={file?.preview}
                        alt={file?.name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                        <Icon name="FileText" size={20} color="var(--color-primary)" strokeWidth={2} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-foreground truncate">{file?.name}</p>
                      <p className="font-caption text-xs text-muted-foreground">
                        {formatFileSize(file?.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file?.id)}
                      className="w-8 h-8 rounded-full hover:bg-danger/10 flex items-center justify-center transition-smooth"
                    >
                      <Icon name="Trash2" size={16} className="text-danger" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div className="space-y-1">
                <p className="font-body text-sm text-foreground font-medium">
                  Important Information
                </p>
                <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Documentation is required to support all violation charges</li>
                  <li>Renters will be notified and can dispute charges within 7 days</li>
                  <li>Platform fee (15%) applies to all violation charges</li>
                  <li>Charges are processed through Stripe Connect</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isSubmitting}
              iconName="DollarSign"
            >
              Create Charge
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddViolationChargeModal;